import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  workspaceName: string;
  impact: { configs: number; reports: number } | null;
  loading: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DisconnectConfirmModal({
  open, workspaceName, impact, loading, error, onCancel, onConfirm,
}: Props) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open, workspaceName]);

  if (!open) return null;
  const matches = typed.trim() === workspaceName;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-bg-card border border-border-default rounded-2xl shadow-selected p-7 w-full max-w-md"
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="font-display text-lg font-semibold">
            Disconnect "{workspaceName}"?
          </h2>
          <button onClick={onCancel} aria-label="Close" className="text-text-muted hover:text-ink-800">×</button>
        </div>

        {loading || !impact ? (
          <p className="text-sm text-text-muted mb-4">Checking impact…</p>
        ) : (
          <>
            <p className="text-sm mb-2">This will permanently delete:</p>
            <ul className="text-sm mb-4 list-disc pl-5">
              <li>{impact.configs} channel configs</li>
              <li>{impact.reports} daily reports</li>
            </ul>
          </>
        )}

        <p className="text-sm mb-2">This cannot be undone.</p>
        <label className="text-xs text-text-secondary block mb-4">
          Type the workspace name to confirm:
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="mt-1 block w-full rounded-md border border-border-default px-3 py-2 text-sm bg-white"
            autoFocus
          />
        </label>

        {error && <div className="text-xs text-danger-text mb-3">{error}</div>}

        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="text-sm px-3 py-2">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={!matches || loading}
            className="text-sm px-4 py-2 rounded-md bg-danger-text text-white disabled:opacity-40"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}
